import { useCallback } from 'react'
import {
  createStateUpdater,
  handleResetFocus,
  resetState,
  clickCreateSideEffect,
  createHandleCreate,
  handleError,
  createHandleSubmit,
  goBackOneModal,
  handleClassNameChange,
  handleGradeChange,
  createHandleSaveEdit,
  createHandleViewClass,
  createHandleCancel,
  createHandleDelete,
  createHandleLicenseChange,
  createValidateForm,
  createHandleHide,
  handleHideSubmit,
} from 'experienceComponents/CreateInstanceShared/sharedStateManagement.js'
import {
  smallGroupFilterConfig,
  smallGroupProgramConfig,
  smallGroupViewClassConfig,
} from './smallGroupConfig.js'
import { checkBlockingValidations } from './data'

// Export the configured functions for CreateSmallGroup
export const handleCreate = createHandleCreate(smallGroupFilterConfig)
export const handleSubmit = createHandleSubmit(
  smallGroupProgramConfig,
  'Small group was not created',
  'ERROR CREATING SMALL GROUP:',
)

export const handleSaveEdit = createHandleSaveEdit(
  'ERROR UPDATING SMALL GROUP:',
)

export const handleViewClass = createHandleViewClass(smallGroupViewClassConfig)
export const handleCancel = createHandleCancel()
export const handleDelete = createHandleDelete('ERROR DELETING SMALL GROUP:')
export const handleLicenseChange = createHandleLicenseChange()
export const validateForm = createValidateForm(checkBlockingValidations)
export const handleHide = createHandleHide('ERROR HIDING SMALL GROUP:')

// Custom hook that creates all callback handlers
export const useCreateSmallGroupHandlers = (state, props, updateState) => {
  const resetStateCallback = useCallback(() => {
    const result = resetState(state, props)
    updateState(result.newState, result.sideEffects)
  }, [state, props, updateState])

  const handleCreateCallback = useCallback(() => {
    const result = handleCreate(state, props)
    updateState(result.newState, result.sideEffects)
  }, [state, props, updateState])

  const handleSubmitCallback = useCallback(async () => {
    const result = await handleSubmit(state, props)
    updateState(result.newState, result.sideEffects)
  }, [state, props, updateState])

  const handleSaveEditCallback = useCallback(async () => {
    const result = await handleSaveEdit(state, props)
    updateState(result.newState, result.sideEffects)
  }, [state, props, updateState])

  const handleDeleteCallback = useCallback(async () => {
    const result = await handleDelete(state, props)
    updateState(result.newState, result.sideEffects)
  }, [state, props, updateState])

  const handleCancelCallback = useCallback(() => {
    const result = handleCancel(state, props)
    updateState(result.newState, result.sideEffects)
  }, [state, props, updateState])

  const handleViewClassCallback = useCallback(() => {
    const result = handleViewClass(state, props)
    updateState(result.newState, result.sideEffects)
  }, [state, props, updateState])

  const handleClassNameChangeCallback = useCallback(
    e => {
      const result = handleClassNameChange(e, state)
      updateState(result.newState, result.sideEffects)
    },
    [state, updateState],
  )

  const handleGradeChangeCallback = useCallback(
    (e, courses) => {
      const result = handleGradeChange(e, courses, state)
      updateState(result.newState, result.sideEffects)
    },
    [state, updateState],
  )

  const handleLicenseChangeCallback = useCallback(
    (e, licenses) => {
      const result = handleLicenseChange(e, licenses, state, props)
      updateState(result.newState, result.sideEffects)
    },
    [state, props, updateState],
  )

  const handleHideSubmitCallback = useCallback(() => {
    const result = handleHideSubmit(state)
    updateState(result.newState, result.sideEffects)
  }, [state, updateState])

  const handleHideCallback = useCallback(async () => {
    const result = await handleHide(state, props)
    updateState(result.newState, result.sideEffects)
  }, [state, props, updateState])

  const goBackOneModalCallback = useCallback(() => {
    const result = goBackOneModal(state)
    updateState(result.newState)
  }, [state, updateState])

  const validateFormCallback = useCallback(async () => {
    const result = await validateForm(state, props)
    updateState(result.newState)
  }, [state, props, updateState])

  return {
    resetStateCallback,
    handleCreateCallback,
    handleSubmitCallback,
    handleSaveEditCallback,
    handleDeleteCallback,
    handleCancelCallback,
    handleViewClassCallback,
    handleClassNameChangeCallback,
    handleGradeChangeCallback,
    handleLicenseChangeCallback,
    goBackOneModalCallback,
    validateFormCallback,
    handleHideCallback,
    handleHideSubmitCallback,
  }
}

// Export other functions that don't need configuration
export {
  createStateUpdater,
  handleResetFocus,
  resetState,
  clickCreateSideEffect,
  handleError,
  goBackOneModal,
  handleClassNameChange,
  handleGradeChange,
}
